<template>
    <svg
        ref="element"
        :class="componentClasses"
        :style="componentStyle"
        :viewBox="`0 0 ${sizes.width} ${sizes.height}`"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            :d="computedPolygonPath"
            :fill="props.fill"
            fill-opacity="1"
        />
        <path
            :d="computedPath"
            fill="none"
            :stroke="props.stroke"
            stroke-width="2"
        />
        <circle
            v-for="(point, index) in points"
            :key="index"
            :cx="point.x"
            :cy="point.y"
            r="3"
            fill="white"
            fill-opacity="1"
            tabindex="0"
            @mouseover="showTooltip(index, $event)"
            @focus="showTooltip(index, $event)"
            @mouseout="hideTooltip"
            @blur="hideTooltip"
        />
        <rect
            v-if="tooltip.visible"
            :x="tooltip.x"
            :y="tooltip.y"
            :width="tooltipWidth"
            :height="tooltipHeight"
            fill="black"
            :rx="5"
            :ry="5"
        />
        <path
            v-if="tooltip.visible"
            :d="trianglePath"
            fill="black"
        />
        <foreignObject
            v-if="tooltip.visible"
            :x="tooltip.x + 10"
            :y="tooltip.y + 12"
            :width="tooltipWidth - 10 - 10"
            :height="tooltipHeight - 12"
            style="text-align: center"
        >
            <span style="color: white;" v-html="tooltip.data" />
        </foreignObject>
    </svg>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import type { Scale } from '@keyvue/one-api-client';

    interface Props {
        dataScale: Scale;
        data: Record<string, number>;
        scaledData: number[];
        backgroundColor?: string;
        stroke?: string;
        fill?: string;
        paddingTop?: number;
        paddingBottom?: number;
    }

    const props = withDefaults(defineProps<Props>(), {
        backgroundColor: '#5756A1',
        stroke: '#01b406',
        fill: '#456a80',
        paddingTop: 0,
        paddingBottom: 0,
    });

    const sizes = ref({
        width: 1000,
        height: 1000,
    });

    const element = ref<SVGElement | null>(null);
    const loaded = ref(false);
    const tooltip = ref({ visible: false, x: 0, y: 0, data: '' });

    const tooltipWidth = 120;
    const tooltipHeight = 40;
    const tooltipAnchorHeight = 8;

    onMounted(() => {
        loaded.value = false;
        sizes.value.width = element.value?.clientWidth ?? 0;
        sizes.value.height = element.value?.clientHeight ?? 0;

        setTimeout(() => {
            loaded.value = true;
            data.value = props.scaledData;
        }, 10);
    });

    const data = ref(props.scaledData.map(() => 0));

    const componentClasses = computed(() => {
        return {
            'linear-graph': true,
            'linear-graph--animating': loaded.value,
        };
    });

    const componentStyle = computed(() => {
        return {
            backgroundColor: props.backgroundColor,
        };
    });

    const points = computed(() => {
        const availableWidth = sizes.value.width;
        const availableHeight = sizes.value.height - props.paddingTop - props.paddingBottom;

        return data.value.map((value, index) => {
            const x = (index / (data.value.length - 1)) * availableWidth;
            const y = availableHeight - (value * availableHeight) + props.paddingTop;

            return { x, y };
        });
    });

    const computedPath = computed(() => {
        const pathPoints = points.value;
        if (pathPoints.length < 2) {
            return '';
        }

        let d = `M${pathPoints[0].x},${pathPoints[0].y}`;
        for (let i = 0; i < pathPoints.length - 1; i += 1) {
            const cp1X = (pathPoints[i].x + pathPoints[i + 1].x) / 2;
            const cp1Y = pathPoints[i].y;
            const cp2X = (pathPoints[i].x + pathPoints[i + 1].x) / 2;
            const cp2Y = pathPoints[i + 1].y;
            d += ` C${cp1X},${cp1Y} ${cp2X},${cp2Y} ${pathPoints[i + 1].x},${pathPoints[i + 1].y}`;
        }

        return d;
    });

    const trianglePath = computed(() => {
        const coordinateX = tooltip.value.x;
        const coordinateY = tooltip.value.y;

        const tipX = coordinateX + tooltipWidth / 2; // Center of the base
        const tipY = coordinateY + tooltipHeight + tooltipAnchorHeight; // Bottom of the rectangle
        const leftBaseX = tipX - tooltipAnchorHeight; // Left corner
        const rightBaseX = tipX + tooltipAnchorHeight; // Right corner

        return `
            M ${leftBaseX},${coordinateY + tooltipHeight}
            L ${rightBaseX},${coordinateY + tooltipHeight}
            L ${tipX},${tipY}
            Z
        `;
    });

    const computedPolygonPath = computed(() => {
        const pathPoints = points.value;
        if (pathPoints.length < 2) {
            return '';
        }

        let d = `M${pathPoints[0].x},${sizes.value.height} L${pathPoints[0].x},${pathPoints[0].y}`;
        for (let i = 0; i < pathPoints.length - 1; i += 1) {
            const cp1X = (pathPoints[i].x + pathPoints[i + 1].x) / 2;
            const cp1Y = pathPoints[i].y;
            const cp2X = (pathPoints[i].x + pathPoints[i + 1].x) / 2;
            const cp2Y = pathPoints[i + 1].y;
            d += ` C${cp1X},${cp1Y} ${cp2X},${cp2Y} ${pathPoints[i + 1].x},${pathPoints[i + 1].y}`;
        }

        d += ` L${pathPoints[pathPoints.length - 1].x},${sizes.value.height} Z`;

        return d;
    });

    function showTooltip(index: number, event: MouseEvent | FocusEvent) {
        let x = 0;
        let y = 0;

        if (event instanceof MouseEvent) {
            x = event.clientX - element.value!.getBoundingClientRect().left;
            y = event.clientY - element.value!.getBoundingClientRect().top;
        } else if (event instanceof FocusEvent) {
            const target = event.target as SVGCircleElement;
            const rect = target.getBoundingClientRect();
            x = rect.left + rect.width / 2 - element.value!.getBoundingClientRect().left;
            y = rect.top - element.value!.getBoundingClientRect().top;
        }

        const key = Object.keys(props.data)[index];
        const formattedKey = formatKey(key, props.dataScale);
        const formattedValue = `<strong>${props.data[key]}</strong> - ${formattedKey}`;

        tooltip.value = {
            visible: true,
            x: x - (tooltipWidth / 2),
            y: y - tooltipHeight - tooltipAnchorHeight * 2,
            data: formattedValue,
        };
    }

    function hideTooltip() {
        setTimeout(() => {
            tooltip.value.visible = false;
        }, 300);
    }

    function formatKey(key: string, scale: Scale): string {
        switch (scale) {
            case 'weeks':
                return key;
            case 'days':
                return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' }).format(new Date(key));
            case 'hours':
                return key;
            default:
                return key;
        }
    }
</script>

<style scoped lang="scss">
.linear-graph {
    width: 100%;
    height: 100%;
    color: color(brand-green);

    circle {
        transition: fill 300ms ease-in-out;
    }

    &--animating {
        path {
            stroke-dasharray: 200vw;
            stroke-dashoffset: 200vw;
            animation: dash 2s linear forwards;
            animation-delay: 800ms;
            transition: d 800ms ease-in-out;

        }

        circle {
            transition: cx 800ms ease-in-out, cy 800ms ease-in-out;
        }
    }
}

@keyframes dash {
    to {
        stroke-dashoffset: 0;
    }
}
</style>
